// Report Header Field Definitions
// Defines all available fields that can be used in report headers

export interface ReportHeaderField {
  id: string;
  label: string;
  description: string;
  dataPath: string; // Path to access the data (e.g., 'patient.pat_name')
  format?: (value: any) => string; // Optional formatter function
}

export const REPORT_HEADER_FIELDS: ReportHeaderField[] = [
  {
    id: 'pat_name',
    label: 'Patient Name',
    description: 'Full name of the patient',
    dataPath: 'patient.pat_name',
  },
  {
    id: 'pat_sex_age',
    label: 'Sex / Age',
    description: 'Patient gender and age',
    dataPath: 'patient', // Special handler needed
    format: (patient) => {
      const sex = patient?.pat_sex || '-';
      const age = patient?.pat_dob ? 'calculated' : '-'; // Will be calculated in generator
      return `${sex} / ${age}`;
    },
  },
  {
    id: 'pat_sex',
    label: 'Sex',
    description: 'Patient gender',
    dataPath: 'patient.pat_sex',
  },
  {
    id: 'pat_age',
    label: 'Age',
    description: 'Patient age',
    dataPath: 'patient.pat_dob', // Will be calculated from DOB
  },
  {
    id: 'pat_pin',
    label: 'YH No.',
    description: 'YH No.',
    dataPath: 'patient.pat_pin',
  },
  {
    id: 'po_acc_no',
    label: 'Acc No',
    description: 'Accession Number',
    dataPath: 'order.po_acc_no',
  },
  {
    id: 'po_ref_doc',
    label: 'Ref Phys.',
    description: 'Referring Physician',
    dataPath: 'order.po_ref_doc',
  },
  {
    id: 'po_pat_class',
    label: 'Reg. Type',
    description: '(Registration / patient class (OPD / IPD / ER)) / / Admission Number',
    dataPath: 'order.po_pat_class',
  },
  {
    id: 'po_modality',
    label: 'Modality',
    description: 'Study Modality (CT, MR, etc.)',
    dataPath: 'order.po_modality',
  },
  {
    id: 'po_req_time',
    label: 'Order Time',
    description: 'Order/Request Date and Time',
    dataPath: 'order.po_req_time',
    format: (value) => 'formatted', // Will be formatted with moment
  },
  {
    id: 'report_created_at',
    label: 'Report Time',
    description: 'Report Creation Date and Time',
    dataPath: 'report.created_at',
    format: (value) => 'formatted', // Will be formatted with moment
  },
  {
    id: 'po_order_no',
    label: 'Order Number',
    description: 'Order/Request Number',
    dataPath: 'order.po_his_ord_no',
  },
  {
    id: 'pat_admission_no',
    label: 'IP Number',
    description: 'Inpatient Number',
    dataPath: 'order.po_visit_number',
  },
  {
    id: 'po_diag_desc',
    label: 'Study Description',
    description: 'Study/Exam Description',
    dataPath: 'order.po_diag_desc',
  },
  {
    id: 'po_clinical_history',
    label: 'Clinical History',
    description: 'Clinical indication/history',
    dataPath: 'order.po_clinical_history',
  },
  {
    id: 'report_status',
    label: 'Report Status',
    description: 'Current status of the report',
    dataPath: 'report.pr_status',
  },
  {
    id: 'assigned_radiologist',
    label: 'Radiologist',
    description: 'Assigned Radiologist',
    dataPath: 'workflow.assigned_radiologist_name',
  },
];

// Helper to get field by ID
export const getFieldById = (fieldId: string): ReportHeaderField | undefined => {
  return REPORT_HEADER_FIELDS.find(field => field.id === fieldId);
};

// Default header configuration (matches current implementation)
export interface HeaderFieldConfig {
  label: string;
  field: string | null;
}

export interface MarginConfig {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * Height (px) of the print-zone bands the PDF reserves for the header and footer,
 * on top of the page margins. The body starts at `margins.top + bands.header` and
 * ends at `margins.bottom + bands.footer`.
 */
export interface BandConfig {
  header: number;
  footer: number;
}

export interface ReportHeaderConfig {
  rows: HeaderFieldConfig[][];
  margins?: MarginConfig;
  bands?: BandConfig;
  showHeaderFooterAllPages?: boolean;
  showFooter?: boolean;
  footerTemplate?: string;
}

export const DEFAULT_BANDS: BandConfig = {
  header: 150,
  footer: 50,
};

export const DEFAULT_HEADER_CONFIG: ReportHeaderConfig = {
  rows: [
    [
      { label: 'Patient Name', field: 'pat_name' },
      { label: 'Sex / Age', field: 'pat_sex_age' },
    ],
    [
      { label: 'YH No.', field: 'pat_pin' },
      { label: 'Acc No', field: 'po_acc_no' },
    ],
    [
      { label: 'Ref Phys.', field: 'po_ref_doc' },
      { label: 'Modality', field: 'po_modality' },
    ],
    [
      { label: 'Order Time', field: 'po_req_time' },
      { label: 'Report Time', field: 'report_created_at' },
    ],
  ],
  margins: {
    top: 20,
    right: 20,
    bottom: 20,
    left: 20,
  },
  bands: {
    header: 150,
    footer: 50,
  },
  showHeaderFooterAllPages: true,
  showFooter: true,
  footerTemplate: 'Note: This is a professional opinion only, Each investigation has its limitations. Final diagnosis needs correlation with clinical context and other investigations. Kindly discuss, if necessary.',
};
